import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import BadgeSheet, { BadgeTile } from '../../components/common/BadgeSheet';
import { apiFormRequest, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyEngagement.css';
import '../user/PublicProfile.css';
import { LockedCreateButton, useCanBuild } from '../../components/common/ModuleInterest';

/* Badges people earn on the engagement score — for the company admin, beside "What counts".

   Two halves: the business unit's standard set (First step … Champion), read-only here with how
   many of OUR people hold each; and the company's OWN badges, made and tuned here. An own badge
   measures what people do in the programme — the score, a family of it, or how often somebody did
   one thing — and is granted at night like every other; "Run now" for the impatient. A new
   badge's first run hands out the past SILENTLY: nobody gets a bell for what they did last month.

   ★ The admin sees the thresholds; the employee sees the badge and, for a count, the rule. The
   score itself stays the admin's — that is why a points badge shows no number on the other side. */

const blank = { name: '', description: '', metric: 'score', threshold: '', value: 10, is_active: true };

const metricLabel = (t, m) => {
  if (m.metric === 'score') return t('badgeMetricScore');
  if (m.metric.startsWith('family:')) return t('badgeMetricFamily', { what: t(`did_${m.label}`) });
  return t('badgeMetricSignal', { what: t(`signal_${m.label}`) });
};

const EngagementBadges = () => {
  const { t } = useTranslation();
  // The paid "Own events" module behind every build button here (null while it loads).
  const canBuild = useCanBuild();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [edit, setEdit] = useState(null);      // the badge being made or changed
  const [file, setFile] = useState(null);
  const [reach, setReach] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => {
    apiRequest('GET', '/company/engagement/badges')
      .then(setData)
      .catch(() => navigate('/member-home'));
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const save = () => {
    if (busy || !edit) return;
    setBusy(true);
    setNotice(null);
    const form = new FormData();
    ['id', 'name', 'description', 'metric', 'threshold', 'value'].forEach((k) => {
      if (edit[k] !== undefined && edit[k] !== null) form.append(k, edit[k]);
    });
    if (edit.is_active) form.append('is_active', 'on');
    if (file) form.append('file', file);
    apiFormRequest('/company/engagement/badges', form)
      .then((res) => { setData(res); setEdit(null); setFile(null); setReach(null); })
      .catch((e) => setNotice(e.message || t('saveFailed')))
      .finally(() => setBusy(false));
  };

  const checkReach = () => {
    if (!edit?.threshold) return;
    apiRequest('POST', '/company/engagement/badges/preview',
               { metric: edit.metric, threshold: edit.threshold })
      .then(setReach)
      .catch(() => setReach(null));
  };

  const runNow = () => {
    if (busy) return;
    setBusy(true);
    apiRequest('POST', '/company/engagement/badges/run')
      .then((res) => { setData(res); setNotice(t('badgeGranted', { count: res.granted || 0 })); })
      .catch((e) => setNotice(e.message || t('saveFailed')))
      .finally(() => setBusy(false));
  };

  if (data === null) {
    return (
      <Base activeNav="home">
        <Card><span className="auth__spinner" /></Card>
      </Base>
    );
  }

  const field = (key, value) => setEdit({ ...edit, [key]: value });

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/engagement" />
        <Title my="1" className="mt-3">
          <Icon name="award" theme="primary" className="me-2" />
          {t('badges')}
        </Title>
        <p className="bravos-q__hint mb-2">{t('engagementBadgesNote')}</p>
        {notice && <p className="bravos-cal__notice">{notice}</p>}
      </Card>

      {data.bu_badges.length > 0 && (
        <Card>
          <Title my="1">{t('engagementBadgesStandard')}</Title>
          <div className="pp-awards-grid">
            {data.bu_badges.map((b) => (
              <BadgeTile key={b.id} badge={b} onOpen={setOpen}>
                <div className="pp-award-event">{t('badgeHoldersN', { count: b.holders })}</div>
              </BadgeTile>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <Title my="1">{t('engagementBadgesOwn')}</Title>
        {data.own.length === 0 && !edit && <p className="ce-note">{t('engagementBadgesOwnEmpty')}</p>}

        {data.own.map((b) => (
          <div key={b.id} className="eng-rule">
            <span className="eng-rule__label">
              {b.picture_url ? <img src={b.picture_url} alt="" style={{ width: 28, height: 28 }} />
                             : <Icon name="award" />}
              {b.name}
              {!b.is_active && <span className="ce-pill ce-pill--draft">{t('badgeRetired')}</span>}
            </span>
            <span className="ce-note m-0">
              {metricLabel(t, data.metrics.find((m) => m.metric === b.metric) || { metric: b.metric, label: '' })}
              {' ≥ '}{b.threshold} · {t('badgeValueOnly', { value: b.value })} · {t('badgeHoldersN', { count: b.holders })}
            </span>
            <button type="button" className="bravos-submit bravos-submit--ghost"
                    onClick={() => { setEdit({ ...b, threshold: b.threshold ?? '' }); setReach(null); setFile(null); }}>
              <Icon name="pencil" />
            </button>
          </div>
        ))}

        {edit ? (
          <div className="bravos-form mt-3">
            <label className="eng-rule__field">
              {t('badgeName')}
              <input type="text" maxLength={50} value={edit.name}
                     onChange={(e) => field('name', e.target.value)} />
            </label>
            <label className="eng-rule__field">
              {t('badgeDescription')}
              <input type="text" maxLength={500} value={edit.description || ''}
                     onChange={(e) => field('description', e.target.value)} />
            </label>
            <label className="eng-rule__field">
              {t('badgeMeasures')}
              <select value={edit.metric} onChange={(e) => { field('metric', e.target.value); setReach(null); }}>
                {data.metrics.map((m) => (
                  <option key={m.metric} value={m.metric}>{metricLabel(t, m)}</option>
                ))}
              </select>
            </label>
            <label className="eng-rule__field">
              {t('badgeThreshold')}
              <input type="number" min={1} value={edit.threshold}
                     onChange={(e) => { field('threshold', e.target.value); setReach(null); }} />
            </label>
            <button type="button" className="bravos-submit bravos-submit--ghost" onClick={checkReach}>
              {t('badgeReach')}
            </button>
            {reach && reach.reach !== null && (
              <span className="ce-note">{t('badgeReachResult', { reach: reach.reach, people: reach.people })}</span>
            )}
            <label className="eng-rule__field">
              {t('badgeValue')}
              <input type="number" min={0} value={edit.value ?? 0}
                     onChange={(e) => field('value', e.target.value)} />
            </label>
            <label className="eng-rule__field">
              {t('badgePicture')}
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </label>
            <label className="ce-check">
              <input type="checkbox" checked={!!edit.is_active}
                     onChange={(e) => field('is_active', e.target.checked)} />
              {t('badgeActive')}
            </label>
            <div className="bravos-form__row">
              <button type="button" className="bravos-submit" disabled={busy || !edit.name || !edit.threshold}
                      onClick={save}>
                {t('save')}
              </button>
              <button type="button" className="bravos-submit bravos-submit--ghost" onClick={() => setEdit(null)}>
                {t('cancel')}
              </button>
            </div>
          </div>
        ) : (
          <div className="bravos-form__row">
            {canBuild === false ? (
              /* Seeing the badges is free, adding one is the paid "Own events" module. */
              <LockedCreateButton label={t('badgeAdd')} />
            ) : (
              <button type="button" className="bravos-submit bravos-submit--ghost"
                      onClick={() => { setEdit({ ...blank }); setReach(null); setFile(null); }}>
                <Icon name="plus" /> {t('badgeAdd')}
              </button>
            )}
            {canBuild !== false && data.own.length > 0 && (
              <button type="button" className="bravos-submit bravos-submit--ghost" disabled={busy} onClick={runNow}>
                <Icon name="play" /> {t('badgeRunNow')}
              </button>
            )}
          </div>
        )}
      </Card>

      {/* Who carries the most badge value here — admin-only, like the score behind it. */}
      {data.ranking?.length > 0 && (
        <Card>
          <Title my="1">{t('badgeRanking')}</Title>
          {data.ranking.map((p, i) => (
            <div key={p.id} className="eng-rule">
              <span className="eng-rule__label">
                <span style={{ width: '1.4rem' }}>{i + 1}</span>
                {p.photo_url
                  ? <img src={p.photo_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                  : <Icon name="user" />}
                {p.full_name}
              </span>
              <span className="ce-note m-0">{t('badgeValueN', { count: p.badges, value: p.value.toLocaleString() })}</span>
            </div>
          ))}
        </Card>
      )}

      <BadgeSheet badge={open} onClose={() => setOpen(null)} />
    </Base>
  );
};

export default EngagementBadges;
